import { Download } from "lucide-react";
import Logo from "@/components/Logo";

// Functionality Report — internal document mirroring CapabilityStatement print styling.
// Reachable via direct URL only (not in public nav).

const SECTIONS = [
  {
    eyebrow: "01 · Architecture",
    title: "Application stack",
    rows: [
      ["Frontend", "React 18 · React Router v6 · TailwindCSS · Framer Motion · shadcn/ui · Lucide"],
      ["Backend", "FastAPI · Pydantic · Motor (async MongoDB)"],
      ["Database", "MongoDB (sandbox) · MongoDB Atlas (production)"],
      ["Deployment", "Kubernetes via Emergent · bigibila-react.emergent.host"],
      ["Custom domain", "www.bigibilasystems.com.au — pending DNS connection"],
      ["Email", "Resend — mocked pending RESEND_API_KEY"],
      ["Analytics", "GA4 privacy-first opt-in — pending Measurement ID"],
    ],
  },
];

const ROUTES = [
  ["/", "Welcome — Bigibila Wiyayl story (after Girribal Gate)"],
  ["/start", "Main hub — Hero, Trust Bar, Spiny Sphere, product cards, founder"],
  ["/story", "Lineage table, Liana ↔ Nan portrait, Arthur Dodd attribution"],
  ["/architecture", "Lore-Led Architecture™ + interactive Spiny Sphere Explorer"],
  ["/standards", "International rights principles → BIGIBILA software controls"],
  ["/toolkit", "Aboriginal Governance Architecture Toolkit™ — 7 BIG-series packs"],
  ["/platform", "Protected Governance Software — 7 modules"],
  ["/demo", "Interactive dashboard — 8 tabs, demo data only"],
  ["/pricing", "Licensing tiers + 4-phase Engagement Timeline"],
  ["/readiness", "6-question self-assessment → tier recommendation"],
  ["/capability-statement", "A4 procurement-ready PDF (this page's sibling)"],
  ["/contact", "Form (persists to DB) + cultural-safety callout"],
  ["/assurance", "Community Responsibility statement"],
  ["/design", "Public design principles"],
  ["/privacy-preferences", "Analytics ON/OFF toggle"],
  ["/legal/terms", "Terms of Use (DRAFT)"],
  ["/legal/privacy", "Privacy Notice (DRAFT)"],
  ["/legal/icip", "Cultural & ICIP Notice (DRAFT)"],
  ["/legal/background-ip", "Background IP Notice + Briana Pitt artwork clause (DRAFT)"],
];

const FEATURES = [
  {
    name: "Girribal Access Gate",
    desc: "Five learning-based questions on story, authority, ICIP, FPIC and data sovereignty. Replaces a password gate. 30-min idle timeout. Filters by cultural respect.",
  },
  {
    name: "Spiny Sphere Explorer",
    desc: "Interactive architecture diagram with 5 clickable spines (FPIC, ICIP, IDS, Escalation, Access Control). Each spine reveals a plain-English explainer + practice examples.",
  },
  {
    name: "Readiness Self-Assessment",
    desc: "Six questions across stage, authority, consent, data sovereignty, breach handling and audit evidence. Scores out of 24, bands into 4 tiers, recommends a pathway.",
  },
  {
    name: "Capability Statement (PDF)",
    desc: "One-click browser print-to-PDF. A4 portrait, page-break-avoid sections, site chrome hidden in print. Same source-of-truth online and offline.",
  },
  {
    name: "Engagement Timeline",
    desc: "Four-phase pathway (Discovery → Architecture → Pilot → Assurance) with durations, deliverables and executive decision gates between each.",
  },
  {
    name: "Privacy-first Analytics",
    desc: "GA4 with strict opt-in consent modal. gtag.js only loads after Accept. IP anonymisation. 5 custom events: gate passed, readiness completed, PDF downloaded, contact submitted, spine clicked.",
  },
];

const EVENTS = [
  ["girribal_gate_passed", "Visitor cleared cultural learning gate"],
  ["readiness_completed", "Score, band, tier"],
  ["capability_pdf_download", "Download PDF clicked"],
  ["contact_submitted", "Product interest captured"],
  ["spiny_spine_clicked", "Which spine"],
];

const API_ROUTES = [
  ["GET", "/api/", "Root API identifier"],
  ["GET", "/api/health", "Readiness probe (no DB calls)"],
  ["POST", "/api/contact", "Persist submission + dispatch email"],
  ["GET", "/api/contact/submissions", "List submissions (admin)"],
  ["GET", "/api/dashboard/stats", "Live hero stats"],
  ["GET", "/api/dashboard/metrics", "Demo dashboard metrics"],
  ["POST", "/api/status", "Health log entry"],
  ["GET", "/api/status", "List health entries"],
];

const QUALITY = [
  ["Frontend lint", "Clean (ESLint, react-hooks/exhaustive-deps)"],
  ["Backend lint", "Clean (ruff)"],
  ["Backend tests", "9/9 passing (pytest)"],
  ["data-testid coverage", "All interactive + critical elements tagged"],
  ["Deployment health", "/api/health probe added, deploy succeeds"],
  ["Mobile responsive", "Verified at 375px across all critical pages"],
  ["MongoDB pattern", "PyObjectId / BaseDocument — no raw _id leaks"],
  ["Env hygiene", "All URLs/keys via .env, no hardcoded URLs"],
];

const GUARDRAILS = [
  ["Not a database of culture", "Protective banner + Assurance page"],
  ["Does not replace Aboriginal authority", "Banner + Standards + ICIP page"],
  ["Demo data only", "Persistent banner on /demo"],
  ["Software, not legal advice", "Contact disclaimer + Terms"],
  ["FPIC for cultural material", "ICIP page + Contact 'Before you write' callout"],
  ["Artist IP protection (Briana Pitt)", "Welcome page + Background IP clause"],
  ["No tracking before consent", "Strict opt-in consent banner"],
  ["No cultural data in analytics", "'What we never track' transparency list"],
];

const PENDING = [
  ["RESEND_API_KEY + DNS", "Required for live contact email delivery"],
  ["REACT_APP_GA_MEASUREMENT_ID", "Required to activate GA4 (placeholder in .env)"],
  ["www.bigibilasystems.com.au DNS", "Awaiting Entri flow in Emergent Domains"],
  ["ABN + phone placeholders", "Capability Statement entity grid + contact block"],
  ["Legal page DRAFT watermarks", "Pending copy sign-off"],
  ["OG card image (1200×630)", "P1 backlog — LinkedIn/email preview"],
];

export default function FunctionalityReport() {
  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-AU", { month: "long", year: "numeric" });

  return (
    <>
      <style>{`
        @page { size: A4 portrait; margin: 14mm 12mm; }
        @media print {
          .protective-banner, header.site-nav, footer.site-footer,
          [data-testid="protective-banner"], [data-testid="site-nav"], [data-testid="site-footer"],
          [data-print-hide="true"] { display: none !important; }
          body, html { background: #ffffff !important; }
          .fr-page { background: #ffffff !important; padding: 0 !important; }
          .fr-section { break-inside: avoid; page-break-inside: avoid; }
          .fr-break-before { break-before: page; page-break-before: always; }
          a { color: inherit; text-decoration: none; }
        }
      `}</style>

      <div className="fr-page bg-stone-100 py-10 md:py-14">
        <div className="mx-auto max-w-[860px] px-4 md:px-0">
          <div
            className="flex flex-wrap items-center justify-between gap-3 mb-6"
            data-print-hide="true"
            data-testid="functionality-actions"
          >
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500">
              Functionality report · Internal · A4 print
            </div>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="functionality-download-pdf"
              className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-3 text-sm font-medium rounded-sm hover:bg-stone-800 transition-colors"
            >
              <Download className="h-4 w-4" /> Download PDF
            </button>
          </div>

          <article className="bg-[#FAFAF9] border border-stone-200 shadow-sm p-10 md:p-14">
            {/* COVER */}
            <header className="fr-section pb-8 border-b border-stone-300">
              <div className="flex items-start justify-between gap-6">
                <Logo />
                <div className="text-right">
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221]">
                    Functionality Report
                  </div>
                  <div className="mt-1 text-[10px] tracking-[0.24em] uppercase text-stone-500">
                    Internal · {formattedDate}
                  </div>
                </div>
              </div>
              <h1 className="mt-10 font-display text-4xl md:text-5xl tracking-tighter font-bold text-stone-900 leading-[1.05]">
                BIGIBILA SYSTEMS™ — what the platform does, top to bottom.
              </h1>
              <p className="mt-5 text-stone-700 leading-relaxed max-w-2xl">
                A complete operating snapshot: architecture, routes, key features, instrumented analytics events, API surface, cultural safety guardrails, quality status and items pending external configuration.
              </p>
            </header>

            {/* ARCHITECTURE */}
            {SECTIONS.map((sec) => (
              <section key={sec.eyebrow} className="fr-section mt-10 pb-8 border-b border-stone-300">
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                  {sec.eyebrow}
                </div>
                <h2 className="font-display text-2xl tracking-tight font-bold text-stone-900 mb-5">
                  {sec.title}
                </h2>
                <div className="grid grid-cols-1 gap-px bg-stone-200 border border-stone-200">
                  {sec.rows.map(([k, v]) => (
                    <div key={k} className="bg-[#FAFAF9] p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                      <div className="md:col-span-3 text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500">
                        {k}
                      </div>
                      <div className="md:col-span-9 text-sm text-stone-800 leading-snug">{v}</div>
                    </div>
                  ))}
                </div>
              </section>
            ))}

            {/* ROUTES */}
            <section className="fr-section mt-10 pb-8 border-b border-stone-300">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                02 · Routes
              </div>
              <h2 className="font-display text-2xl tracking-tight font-bold text-stone-900 mb-5">
                {ROUTES.length} routes delivering story → architecture → product → evidence → conversion.
              </h2>
              <div className="grid grid-cols-1 gap-px bg-stone-200 border border-stone-200">
                {ROUTES.map(([path, desc]) => (
                  <div key={path} className="bg-[#FAFAF9] p-3 grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-4 font-mono text-[12px] text-[#9C4221] font-semibold leading-snug">
                      {path}
                    </div>
                    <div className="md:col-span-8 text-[13px] text-stone-700 leading-snug">{desc}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* KEY FEATURES */}
            <section className="fr-section mt-10 pb-8 border-b border-stone-300">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                03 · Key features
              </div>
              <h2 className="font-display text-2xl tracking-tight font-bold text-stone-900 mb-5">
                Six distinctive capabilities.
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-200 border border-stone-200">
                {FEATURES.map((f) => (
                  <div key={f.name} className="bg-[#FAFAF9] p-4">
                    <h3 className="font-display font-bold text-base tracking-tight text-stone-900 leading-snug">
                      {f.name}
                    </h3>
                    <p className="mt-2 text-[12px] text-stone-700 leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ANALYTICS EVENTS */}
            <section className="fr-section mt-10 pb-8 border-b border-stone-300">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                04 · Instrumented analytics events
              </div>
              <p className="text-sm text-stone-700 mb-4 leading-relaxed">
                Privacy-first GA4. Tracking is gated by explicit visitor consent; IP is anonymised; no PII or cultural data is collected.
              </p>
              <div className="grid grid-cols-1 gap-px bg-stone-200 border border-stone-200">
                {EVENTS.map(([name, payload]) => (
                  <div key={name} className="bg-[#FAFAF9] p-3 grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-5 font-mono text-[12px] text-[#9C4221] font-semibold leading-snug">
                      {name}
                    </div>
                    <div className="md:col-span-7 text-[12px] text-stone-700 leading-snug">{payload}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* API */}
            <section className="fr-section mt-10 pb-8 border-b border-stone-300">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                05 · Backend API surface
              </div>
              <div className="grid grid-cols-1 gap-px bg-stone-200 border border-stone-200">
                {API_ROUTES.map(([method, path, desc]) => (
                  <div key={`${method}-${path}`} className="bg-[#FAFAF9] p-3 grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-1 text-[10px] tracking-[0.22em] uppercase font-bold text-[#9C4221]">
                      {method}
                    </div>
                    <div className="md:col-span-4 font-mono text-[12px] text-stone-900 leading-snug">{path}</div>
                    <div className="md:col-span-7 text-[12px] text-stone-700 leading-snug">{desc}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* CULTURAL SAFETY */}
            <section className="fr-section mt-10 pb-8 border-b border-stone-300">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                06 · Cultural safety guardrails
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-200 border border-stone-200">
                {GUARDRAILS.map(([rule, where]) => (
                  <div key={rule} className="bg-[#FAFAF9] p-3">
                    <div className="font-display font-bold text-[13px] tracking-tight text-stone-900 leading-snug">
                      {rule}
                    </div>
                    <div className="mt-1 text-[11px] text-stone-600 leading-relaxed">{where}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* QUALITY */}
            <section className="fr-section mt-10 pb-8 border-b border-stone-300">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                07 · Quality, testing & deployment
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-200 border border-stone-200">
                {QUALITY.map(([k, v]) => (
                  <div key={k} className="bg-[#FAFAF9] p-3">
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500">{k}</div>
                    <div className="mt-1 text-[13px] text-stone-900 leading-snug">{v}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* PENDING */}
            <section className="fr-section mt-10">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                08 · Pending external configuration
              </div>
              <p className="text-sm text-stone-700 mb-4 leading-relaxed">
                The codebase is complete and deployed. The items below depend on credentials, DNS or copy sign-off — not engineering work.
              </p>
              <div className="grid grid-cols-1 gap-px bg-stone-200 border border-stone-200">
                {PENDING.map(([item, reason]) => (
                  <div key={item} className="bg-[#FAFAF9] p-3 grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-5 font-display font-bold text-[13px] tracking-tight text-stone-900 leading-snug">
                      {item}
                    </div>
                    <div className="md:col-span-7 text-[12px] text-stone-700 leading-relaxed">{reason}</div>
                  </div>
                ))}
              </div>

              <p className="mt-8 text-[11px] text-stone-500 leading-relaxed border-t border-stone-200 pt-5">
                BIGIBILA SYSTEMS™, Bigibila Advisory Method™, Lore-Led Architecture™, Aboriginal Governance Architecture Model™ and AGAM Toolkit™ are trademarks of Bigibila Consultancy. This is an internal operating snapshot, not legal advice.
              </p>
              <p className="mt-3 text-[10px] tracking-[0.24em] uppercase font-bold text-stone-500">
                Strong Foundations · Living Governance · Sovereign Futures
              </p>
            </section>
          </article>
        </div>
      </div>
    </>
  );
}
